'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { syncCartCookie, syncWishlistCookie } from '@/lib/clientStore';
import { ChevronDown, Heart, AlertTriangle, Gift, UserPlus, Clock, Check, Flame, ShoppingCart, KeyRound, Zap, Hourglass } from 'lucide-react';
import type { ClientRegionPrice, OfferType, CartItem, WishlistItem } from '@/types/pdp';
import { usePriceConvert } from '@/lib/PriceContext';
import { msrpUsdFromDiscount } from '@/lib/pricing';
import { t } from '@/lib/i18n';
import { useT } from '@/hooks/useT';

type RegionMetaEntry = { flag: string; names: Record<string,string> };
const REGION_META: Record<string, RegionMetaEntry> = {
  AR: { flag: '🇦🇷', names: { en: 'Argentina',       uk: 'Аргентина',         ru: 'Аргентина'          } },
  AU: { flag: '🇦🇺', names: { en: 'Australia',        uk: 'Австралія',         ru: 'Австралия'          } },
  BR: { flag: '🇧🇷', names: { en: 'Brazil',           uk: 'Бразилія',          ru: 'Бразилия'           } },
  CA: { flag: '🇨🇦', names: { en: 'Canada',           uk: 'Канада',            ru: 'Канада'             } },
  CL: { flag: '🇨🇱', names: { en: 'Chile',            uk: 'Чилі',              ru: 'Чили'               } },
  CO: { flag: '🇨🇴', names: { en: 'Colombia',         uk: 'Колумбія',          ru: 'Колумбия'           } },
  GB: { flag: '🇬🇧', names: { en: 'United Kingdom',   uk: 'Велика Британія',   ru: 'Великобритания'     } },
  HK: { flag: '🇭🇰', names: { en: 'Hong Kong',        uk: 'Гонконг',           ru: 'Гонконг'            } },
  IN: { flag: '🇮🇳', names: { en: 'India',            uk: 'Індія',             ru: 'Индия'              } },
  JP: { flag: '🇯🇵', names: { en: 'Japan',            uk: 'Японія',            ru: 'Япония'             } },
  KR: { flag: '🇰🇷', names: { en: 'South Korea',      uk: 'Корея',             ru: 'Корея'              } },
  MX: { flag: '🇲🇽', names: { en: 'Mexico',           uk: 'Мексика',           ru: 'Мексика'            } },
  NO: { flag: '🇳🇴', names: { en: 'Norway',           uk: 'Норвегія',          ru: 'Норвегия'           } },
  NZ: { flag: '🇳🇿', names: { en: 'New Zealand',      uk: 'Нова Зеландія',     ru: 'Новая Зеландия'     } },
  RU: { flag: '🇷🇺', names: { en: 'Russia',           uk: 'Росія',             ru: 'Россия'             } },
  SA: { flag: '🇸🇦', names: { en: 'Saudi Arabia',     uk: 'Саудівська Аравія', ru: 'Саудовская Аравия'  } },
  SE: { flag: '🇸🇪', names: { en: 'Sweden',           uk: 'Швеція',            ru: 'Швеция'             } },
  SG: { flag: '🇸🇬', names: { en: 'Singapore',        uk: 'Сінгапур',          ru: 'Сингапур'           } },
  TR: { flag: '🇹🇷', names: { en: 'Turkey',           uk: 'Туреччина',         ru: 'Турция'             } },
  TW: { flag: '🇹🇼', names: { en: 'Taiwan',           uk: 'Тайвань',           ru: 'Тайвань'            } },
  UA: { flag: '🇺🇦', names: { en: 'Ukraine',          uk: 'Україна',           ru: 'Украина'            } },
  US: { flag: '🇺🇸', names: { en: 'United States',    uk: 'США',               ru: 'США'                } },
  ZA: { flag: '🇿🇦', names: { en: 'South Africa',     uk: 'ПАР',               ru: 'ЮАР'               } },
};
function getRegionName(meta: RegionMetaEntry | undefined, lang: string, code: string): string {
  if (!meta) return code;
  return meta.names[lang] ?? meta.names['uk'] ?? code;
}


const RENTAL_OPTIONS_FALLBACK = [
  { days: 7,  mult: 0.40, label: '7д', popular: false },
  { days: 14, mult: 0.65, label: '14д', popular: true },
  { days: 30, mult: 1.20, label: '30д', popular: false },
];

type ApiRentalOption = {
  id: number;
  days: number;
  label: string;
  badge_text: string | null;
  price_usd: number | null;
  price_per_day: number | null;
  original_price: number | null;
  discount_percent: number;
  price_multiplier: number;
};


function computeCountdown(endUtc: string | null, lang: string): { text: string; fill: number } {
  if (!endUtc) return { text: '', fill: 0 };
  const diff = new Date(endUtc).getTime() - Date.now();
  if (diff <= 0) return { text: t('pdp_sale_ended', lang), fill: 100 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const text = d > 0
    ? `${d} ${t('cd_days', lang)} ${h} ${t('cd_hours', lang)}`
    : `${h} ${t('cd_hours', lang)} ${Math.floor((diff % 3600000) / 60000)} ${t('cd_minutes', lang)}`;
  // rounded so the SSR value and the client value match (no hydration diff from ms-level Date.now drift)
  const fill = Math.round(Math.max(5, Math.min(92, 100 - (diff / (30 * 86400000)) * 100)));
  return { text, fill };
}

function useCountdown(endUtc: string | null, lang: string) {
  // Compute synchronously on the FIRST render (SSR + client) so the sale block
  // shows immediately — no late pop-in / layout shift after mount.
  const [state, setState] = useState(() => computeCountdown(endUtc, lang));
  useEffect(() => {
    if (!endUtc) return;
    setState(computeCountdown(endUtc, lang));
    const id = setInterval(() => setState(computeCountdown(endUtc, lang)), 60000);
    return () => clearInterval(id);
  }, [endUtc, lang]);
  return state;
}

interface Props {
  prices: ClientRegionPrice[];
  title: string;
  productId: string;
  thumb: string;
  productType: 'game' | 'dlc';
  isPurchasable?: boolean | null;
  isFree?: boolean;
  rating: number | null;
  platforms: string[];
  capabilities: string[];
  developerName: string | null;
  releaseYear: number | null;
  releaseAt?: string | null;
  isUnreleased?: boolean;
  xboxLiveGold?: boolean;
  blockedOffers?: Record<string, string[]>;
  displaySettings?: { rubRate: number; rubSource: string; showUsd: boolean; showRub: boolean; marketChips?: Record<string, { uk: string[]; en: string[] }>; rentalSettings?: { base_region: string; options: Array<{ days: number; mult: number; label: string; popular?: boolean; badge_text?: string | null }> }; offerFeatures?: Record<string, { ru?: string[]; uk?: string[]; en?: string[] }> };
}

export function PurchasePanel({ prices, title, productId, thumb, productType, isPurchasable, isFree, rating, platforms, capabilities, developerName, releaseYear, releaseAt, isUnreleased, xboxLiveGold, blockedOffers, displaySettings }: Props) {
  const searchParams = useSearchParams();
  const { lang, displayRates } = usePriceConvert();
  const tl = useT();

  // Feature-буліти: спершу з адмінки (offer-types таб, по мові), fallback на i18n-дефолти.
  // Порожні рядки в збереженому наборі відкидаємо; якщо нічого валідного — беремо i18n.
  const featFor = (offerType: string, fallbackKeys: [string, string, string]): string[] => {
    const byLang = displaySettings?.offerFeatures?.[offerType];
    const arr = (byLang?.[lang as 'ru' | 'uk' | 'en'] ?? []).map(s => (s ?? '').trim()).filter(Boolean);
    return arr.length ? arr : fallbackKeys.map(k => tl(k as Parameters<typeof tl>[0]));
  };

  const SUBTABS = [
    {
      id: 'key' as const, label: tl('delivery_key'),
      Icon: KeyRound, colorClass: 'c-g',
      activeBorder: 'rgba(82,176,67,.35)', activeGlow: 'rgba(82,176,67,.2)',
      ctaBg: 'var(--gs-green-grad)', ctaGlow: 'rgba(16,185,129,.55)', ctaHover: '#22c55e',
      ctaLabel: tl('pdp_buy_key'), ctaIcon: ShoppingCart,
      featColor: 'var(--gs-green)',
      features: featFor('key', ['feat_key_1', 'feat_key_2', 'feat_key_3']),
      offerType: 'key' as OfferType,
    },
    {
      id: 'gift' as const, label: tl('delivery_gift'),
      Icon: Gift, colorClass: 'c-b',
      activeBorder: 'rgba(0,120,212,.35)', activeGlow: 'rgba(0,120,212,.2)',
      ctaBg: '#0078d4', ctaGlow: 'rgba(0,120,212,.5)', ctaHover: '#1a8fe3',
      ctaLabel: tl('pdp_buy_gift'), ctaIcon: Gift,
      featColor: '#4da6ff',
      features: featFor('gift', ['feat_gift_1', 'feat_gift_2', 'feat_gift_3']),
      offerType: 'gift' as OfferType,
    },
    {
      id: 'account' as const, label: tl('delivery_account'),
      Icon: UserPlus, colorClass: 'c-g',
      activeBorder: 'rgba(82,176,67,.35)', activeGlow: 'rgba(82,176,67,.2)',
      ctaBg: 'var(--gs-green-grad)', ctaGlow: 'rgba(16,185,129,.55)', ctaHover: '#22c55e',
      ctaLabel: tl('pdp_buy_account'), ctaIcon: UserPlus,
      featColor: 'var(--gs-green)',
      features: featFor('account', ['feat_account_1', 'feat_account_2', 'feat_account_3']),
      offerType: 'account' as OfferType,
    },
  ];

  const RENT_FEATURES = featFor('rental', ['feat_rental_1', 'feat_rental_2', 'feat_rental_3']);

  const urlType   = searchParams.get('offer')  ?? searchParams.get('type');
  const urlRegion = searchParams.get('region') ?? searchParams.get('r');
  const initMode: 'buy' | 'rent' = urlType === 'rental' ? 'rent' : 'buy';
  const initSubTab: 'key' | 'gift' | 'account' =
    urlType === 'gift' ? 'gift' : urlType === 'account' ? 'account' : 'key';
  // За замовчуванням відкриваємо НАЙДЕШЕВШИЙ регіон (за ціною дефолтного типу видачі) —
  // щоб PDP збігався з «від»-ціною на картках/каталозі. Виключаємо регіони, де цей тип
  // видачі заблоковано в /admin/settings?tab=regions (повністю заблоковані вже відсіяні
  // сервером через blocked_markets). Клієнт може змінити регіон вручну.
  const initOffer = initMode === 'rent' ? 'rental' : initSubTab;
  const cheapestMarket = prices
    .filter(p => p.prices_usd?.[initOffer] != null && !(blockedOffers?.[p.market_code] ?? []).includes(initOffer))
    .reduce<{ code: string; usd: number } | null>((best, p) => {
      const usd = p.prices_usd![initOffer] as number;
      return (!best || usd < best.usd) ? { code: p.market_code, usd } : best;
    }, null)?.code;
  const initMarket = (urlRegion && prices.find(p => p.market_code === urlRegion))
    ? urlRegion
    : (cheapestMarket ?? prices.find(p => p.market_code === 'US')?.market_code ?? prices[0]?.market_code ?? 'US');
  const [market, setMarket]           = useState(initMarket);
  const [regionOpen, setRegionOpen]   = useState(false);
  const [mode, setMode]               = useState<'buy' | 'rent'>(initMode);
  const [subTab, setSubTab]           = useState<'key' | 'gift' | 'account'>(initSubTab);
  const [rentalDays, setRentalDays]   = useState(7);
  const [wished, setWished]           = useState(false);
  const [added, setAdded]             = useState(false);
  const [apiRentalOpts, setApiRentalOpts] = useState<ApiRentalOption[] | null>(null);
  const [deliveryStock, setDeliveryStock] = useState<{ key: boolean; key_regions: string[]; gift: boolean; account: boolean; account_regions: string[]; rental: boolean } | null>(null);

  const visiblePrices = useMemo(() => {
    if (!blockedOffers) return prices;
    const offerKey = mode === 'rent' ? 'rental' : subTab;
    return prices.filter(p => {
      const bl = blockedOffers[p.market_code] ?? [];
      return !bl.includes('all') && !bl.includes(offerKey);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, blockedOffers, mode, subTab]);

  const price      = visiblePrices.find(p => p.market_code === market) ?? visiblePrices[0] ?? prices[0];

  // Deep-link: тримаємо URL синхронним із вибором типу видачі + регіону (?offer=&region=),
  // щоб клієнт міг скопіювати пряме посилання на конкретний тип/регіон. shallow — replaceState
  // без навігації (не перезавантажує серверні дані). Дефолти (key / поточний cheapest-market) опускаємо.
  useEffect(() => {
    const offer = mode === 'rent' ? 'rental' : subTab;
    const p = new URLSearchParams(window.location.search);
    p.delete('type'); p.delete('r'); // legacy
    if (offer === 'key') p.delete('offer'); else p.set('offer', offer);
    if (market) p.set('region', market); else p.delete('region');
    const qs = p.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, '', next);
    }
  }, [subTab, mode, market]);

  // Single source of truth: pre-computed USD from worker (product_offer_prices).
  // Falls back to live fx only if pre-computed value is missing.
  // Клієнт отримує ЛИШЕ готові USD-ціни (prices_usd) — без базової ціни й націнок.
  const getUsd = (p: ClientRegionPrice, offerKey: string): number | null => p.prices_usd?.[offerKey] ?? null;
  // Централізовано: конвертація USD→₽/₴ через той самий displayRates (PriceContext),
  // що й каталог/кошик/чекаут — з націнкою курсу валюти (напр. RUB +5%). Раніше PDP
  // рахував по сирому rubRate/fxRates і тому розходився з рештою сайту.
  const convDisp = (usd: number | null, code: 'RUB' | 'UAH'): number | null => {
    if (usd == null) return null;
    const r = displayRates?.[code];
    if (r) return usd * r.value * (1 + r.markup / 100);
    if (code === 'RUB' && displaySettings) return usd * displaySettings.rubRate;
    return null;
  };
  const getRub = (usd: number | null): number | null => convDisp(usd, 'RUB');
  const getUah = (usd: number | null): number | null => convDisp(usd, 'UAH');

  const meta       = REGION_META[price?.market_code ?? ''];
  const disc       = price?.discount_percent != null ? Math.round(price.discount_percent) : null;
  const isUrgent   = price?.offer_end_utc ? (new Date(price.offer_end_utc).getTime() - Date.now()) < 86400000 * 3 : false;
  const { text: countdown, fill: saleBarFill } = useCountdown(price?.offer_end_utc ?? null, lang);

  // Load rental options from API
  useEffect(() => {
    const baseRegion = displaySettings?.rentalSettings?.base_region ?? market;
    fetch(`/api/rental-options?product_id=${productId}&region=${baseRegion}`)
      .then(r => r.ok ? r.json() : null)
      .then((data: ApiRentalOption[] | null) => {
        if (data && data.length > 0) {
          setApiRentalOpts(data);
          // Set default rental days to first option with badge or first option
          const defaultOpt = data.find(o => o.badge_text) ?? data[0];
          if (defaultOpt) setRentalDays(defaultOpt.days);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Load delivery stock (region-aware: keys & accounts by market_code, rental by any free account)
  useEffect(() => {
    fetch(`/api/delivery-stock?product_id=${productId}&region=${market}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDeliveryStock(data); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, market]);

  // Use API options if loaded, otherwise fall back to displaySettings (SSR) or hardcoded.
  // ВАЖЛИВО: `?? FALLBACK` не спрацьовує на порожньому масиві — тому перевіряємо .length,
  // інакше опції оренди лишались порожні до завантаження /api/rental-options (візуальна затримка).
  const ssrRentalOpts = displaySettings?.rentalSettings?.options;
  const legacyRentalOpts = ssrRentalOpts && ssrRentalOpts.length ? ssrRentalOpts : RENTAL_OPTIONS_FALLBACK;
  // ВАЖЛИВО: admin.rental_options.label/badge_text — вільний текст в ОДНІЙ мові (той, що ввів
  // адмін), не локалізований per-language. Раніше рендерились як є → на ru/en сторінках теж
  // показувало українське "7 днів"/"Вигідно" незалежно від обраної мови (знайдено користувачем
  // 2026-09-22 на прикладі 9MZVD0JQ0FDH?region=UA). label ігноруємо повністю (рахуємо з
  // opt.days нижче), badge_text лишаємо лише як ТРИГЕР "позначити цю опцію" (будь-який
  // непустий текст від адміна) — сам текст бейджа завжди локалізований (tl('pdp_popular')).
  const activeRentalOpts = apiRentalOpts
    ? apiRentalOpts.map(o => ({ days: o.days, mult: o.price_multiplier, label: o.label, popular: !!o.badge_text, badge_text: o.badge_text }))
    : legacyRentalOpts.map(o => ({ ...o, badge_text: (o as { badge_text?: string }).badge_text ?? null }));

  const rentalOpt = activeRentalOpts.find(r => r.days === rentalDays) ?? activeRentalOpts[0] ?? { days: 7, mult: 0.40, label: '7д', popular: false, badge_text: null };

  // If API returned pricing, use it directly; otherwise compute from base price
  const apiCurrentOpt = apiRentalOpts?.find(o => o.days === rentalDays);
  // Базова USD-ціна оренди (вже з націнкою) — з prices_usd базового регіону.
  const rentalBaseUsd = displaySettings?.rentalSettings?.base_region
    ? (prices.find(p => p.market_code === displaySettings.rentalSettings!.base_region)?.prices_usd?.rental ?? price?.prices_usd?.rental)
    : price?.prices_usd?.rental;
  const rentalPriceFromApi = apiCurrentOpt?.price_usd;
  // rentalPrice — завжди в USD (API або prices_usd × множник тарифу).
  const rentalPrice = rentalPriceFromApi != null
    ? rentalPriceFromApi
    : (rentalBaseUsd != null ? rentalBaseUsd * rentalOpt.mult : 0);
  const rentalPerDay = apiCurrentOpt?.price_per_day ?? (rentalDays > 0 ? rentalPrice / rentalDays : 0);
  const rentSavings = apiCurrentOpt?.discount_percent
    ? Math.round(apiCurrentOpt.discount_percent)
    : Math.max(0, Math.round((1 - rentalOpt.mult) * 100));

  // Converts USD amount → display string (RUB or USD) matching the Buy panel logic
  const cvtFromUsd = (usd: number | null): string | null => {
    if (usd == null) return null;
    if (lang === 'ru' && displaySettings?.showRub) {
      const r = getRub(usd);
      if (r != null) return `${Math.round(r).toLocaleString('ru-RU')} ₽`;
    }
    if (lang === 'uk') {
      const h = getUah(usd);
      if (h != null) return `${Math.round(h).toLocaleString('uk-UA')} ₴`;
    }
    return `$${usd.toFixed(2)}`;
  };

  const st = SUBTABS.find(s => s.id === subTab)!;

  useEffect(() => {
    try {
      const currentOfferType = mode === 'rent' ? 'rent' : subTab;
      const wl = JSON.parse(localStorage.getItem('wishlist') || '[]') as { product_id: string; offer_type?: string }[];
      setWished(wl.some(i => i.product_id === productId && i.offer_type === currentOfferType));
    } catch {}
  }, [productId, subTab, mode]);

  function handleAddToCart() {
    if (!price) return;
    const offerType: OfferType = mode === 'rent' ? 'rental' : st.offerType;
    try {
      const cart: CartItem[] = JSON.parse(localStorage.getItem('cart') || '[]');
      // Ціна для кошика — готова USD-ціна (mode rent → фінальна оренда). msrp виводимо
      // зі знижки, без розкриття бази. currency_code='USD' (конвертація на відображенні).
      const offerKey = mode === 'rent' ? 'rental' : subTab;
      const usdMain = mode === 'rent' ? rentalPrice : getUsd(price, offerKey);
      const usdMsrp = msrpUsdFromDiscount(usdMain ?? null, price.discount_percent);
      if (usdMain == null) return;
      // Оренда: економія vs купівля — порівнюємо ціну оренди з НАЙДЕШЕВШИМ ключем
      // серед усіх регіонів (не лише поточного). Для рядка «Економія X% vs купівля».
      let rentalSavePct: number | null = null;
      if (mode === 'rent') {
        let cheapestKeyUsd: number | null = null;
        for (const p of prices) {
          if ((blockedOffers?.[p.market_code] ?? []).some(b => b === 'all' || b === 'key')) continue;
          const k = getUsd(p, 'key');
          if (k != null && k > 0 && (cheapestKeyUsd == null || k < cheapestKeyUsd)) cheapestKeyUsd = k;
        }
        if (cheapestKeyUsd != null && usdMain < cheapestKeyUsd) {
          rentalSavePct = Math.round((1 - usdMain / cheapestKeyUsd) * 100);
        }
      }
      // Різні періоди оренди (7/14/30) — це РІЗНІ позиції (різні ціни). Ключ дедупу
      // має збігатися з lineId у кошику: product + offer + market + rental_days.
      const rentDays = mode === 'rent' ? rentalDays : undefined;
      const existing = cart.find(i =>
        i.product_id === productId && i.offer_type === offerType && i.market_code === market &&
        (offerType !== 'rental' || (i.rental_days ?? undefined) === rentDays));
      if (existing) { existing.quantity += 1; }
      else {
        cart.push({
          product_id: productId, title, thumb_url: thumb, product_type: productType,
          offer_type: offerType, market_code: market,
          currency_code: 'USD',
          list_price: usdMain,
          msrp: usdMsrp,
          discount_percent: price.discount_percent,
          rental_days: mode === 'rent' ? rentalDays : undefined,
          rental_save_pct: rentalSavePct,
          rating, platforms, capabilities,
          developer_name: developerName, release_year: releaseYear,
          xbox_live_gold_required: xboxLiveGold,
          quantity: 1, added_at: new Date().toISOString(),
        });
      }
      localStorage.setItem('cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cart-updated'));
    } catch {}
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  function handleWishlist() {
    try {
      const wl: WishlistItem[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
      const offerType = mode === 'rent' ? 'rent' as const : (st.offerType === 'rental' ? 'key' as const : st.offerType as 'key' | 'gift' | 'account');
      if (wished) {
        const idx = wl.findIndex(i => i.product_id === productId && i.offer_type === offerType);
        if (idx >= 0) wl.splice(idx, 1);
        setWished(false);
      } else {
        if (!price) return;
        // Готова USD-ціна (як кошик); msrp — зі знижки, без розкриття бази.
        const offerKey = mode === 'rent' ? 'rental' : subTab;
        const usdMain = mode === 'rent' ? rentalPrice : getUsd(price, offerKey);
        const usdMsrp = msrpUsdFromDiscount(usdMain ?? null, price.discount_percent);
        if (usdMain == null) return;
        wl.push({
          product_id: productId, title, thumb_url: thumb, product_type: productType,
          market_code: market, currency_code: 'USD',
          list_price: usdMain,
          msrp: usdMsrp,
          discount_percent: price.discount_percent, offer_end_utc: price.offer_end_utc,
          rating: rating, platforms: platforms, capabilities: capabilities,
          developer_name: developerName, release_year: releaseYear,
          offer_type: offerType, rent_days: mode === 'rent' ? rentalDays : null,
          added_at: new Date().toISOString(),
        });
        setWished(true);
      }
      localStorage.setItem('wishlist', JSON.stringify(wl));
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch {}
  }

  const ctaBg = added ? 'var(--gs-green-grad)' : (mode === 'rent' ? '#ea6c00' : st.ctaBg);
  const ctaGlow = added ? 'rgba(82,176,67,.5)' : (mode === 'rent' ? 'rgba(251,146,60,.5)' : st.ctaGlow);

  // No purchasable prices. Distinguish three cases:
  //   1. Unreleased / future-release → "Coming soon" (price not announced yet)
  //   2. DLC or non-purchasable       → "Недоступно"
  //   3. Genuinely free, released     → "Free to Play"
  const noPrices = prices.length === 0;
  // releaseAt is nulled by the API for the 9998 "date unknown" placeholder, so rely
  // on the dedicated is_unreleased flag (release_at > NOW()) to detect upcoming games.
  const releaseTs = releaseAt ? new Date(releaseAt).getTime() : NaN;
  const hasRealFutureDate = !Number.isNaN(releaseTs) && releaseTs > Date.now();

  if (noPrices && isUnreleased) {
    // Show concrete date only when the API kept a real announced date (year < 2100).
    // The 9998 placeholder ("date unknown") → no date line, just "Coming soon".
    const dateStr = hasRealFutureDate
      ? new Date(releaseTs).toLocaleDateString(lang === 'ru' ? 'ru-RU' : lang === 'en' ? 'en-US' : 'uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;
    return (
      <div className="pp-card" style={{ borderRadius: 16, border: '1px solid var(--v4-border)', background: 'var(--surface)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', zIndex: 2, boxShadow: '0 20px 60px rgba(0,0,0,.5)', width: '100%', boxSizing: 'border-box', minWidth: 0 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--v4-fg-3)', marginBottom: 3 }}>{tl('pdp_tech_release_date')}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--gs-green)', letterSpacing: '-.01em' }}>
            {dateStr ? tl('pdp_coming_soon_date').replace('{date}', dateStr) : tl('pdp_coming_soon')}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--v4-fg-4)', borderTop: '1px solid var(--v4-border)', paddingTop: 10 }}>
          {tl('pdp_coming_soon_desc')}
        </div>
      </div>
    );
  }

  if (noPrices) {
    // A genuinely-free item (sale_type='free') is AVAILABLE → "Free to Play",
    // even for DLC. Only treat as unavailable when it's NOT free.
    const isUnavailable = !isFree && (productType === 'dlc' || isPurchasable === false);
    return (
      <div className="pp-card" style={{ borderRadius: 16, border: '1px solid var(--v4-border)', background: 'var(--surface)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', zIndex: 2, boxShadow: '0 20px 60px rgba(0,0,0,.5)', width: '100%', boxSizing: 'border-box', minWidth: 0 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--v4-fg-3)', marginBottom: 3 }}>{tl('pdp_price_label')}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: isUnavailable ? 'var(--v4-fg-3)' : 'var(--gs-price)', letterSpacing: '-.01em' }}>{isUnavailable ? tl('pdp_unavailable') : 'Free to Play'}</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--v4-fg-4)', borderTop: '1px solid var(--v4-border)', paddingTop: 10 }}>
          {isUnavailable ? tl('pdp_unavailable_content') : tl('pdp_free_game_note')}
        </div>
      </div>
    );
  }

  return (
    <div className="pp-card" style={{ borderRadius: 16, border: '1px solid var(--v4-border)', background: 'var(--surface)', padding: 18, display: 'flex', flexDirection: 'column', gap: 16, position: 'relative', zIndex: 2, boxShadow: '0 20px 60px rgba(0,0,0,.5)', width: '100%', boxSizing: 'border-box', minWidth: 0 }}>

      {/* -- Sale countdown bar -- */}
      {price?.offer_end_utc && countdown && countdown !== t('pdp_sale_ended', lang) && (
        <div style={{
          borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
          background: isUrgent ? 'linear-gradient(135deg,rgba(239,68,68,.13),rgba(220,38,38,.06))' : 'linear-gradient(135deg,rgba(245,158,11,.1),rgba(239,68,68,.06))',
          border: `1px solid ${isUrgent ? 'rgba(239,68,68,.28)' : 'rgba(245,158,11,.2)'}`,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '40%', background: isUrgent ? 'linear-gradient(90deg,rgba(239,68,68,.08),transparent)' : 'linear-gradient(90deg,rgba(245,158,11,.07),transparent)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, position: 'relative' }}>
            <Flame size={15} color={isUrgent ? '#ef4444' : '#f59e0b'} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
              <span className="pp-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--v4-fg-3)', whiteSpace: 'nowrap' }}>{tl('pdp_sale_active')}</span>
              <span suppressHydrationWarning style={{ fontSize: 12, fontWeight: 800, color: isUrgent ? '#f87171' : '#fbbf24', whiteSpace: 'nowrap' }}>{countdown}</span>
            </div>
            {disc && disc > 0 && (
              <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: isUrgent ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.15)', border: `1px solid ${isUrgent ? 'rgba(239,68,68,.28)' : 'rgba(245,158,11,.28)'}`, color: isUrgent ? '#f87171' : '#fbbf24' }}>
                -{disc}%
              </span>
            )}
          </div>
          <div style={{ height: 3, background: 'var(--v4-panel-2)', borderRadius: 999, overflow: 'hidden' }}>
            <div suppressHydrationWarning style={{ height: '100%', borderRadius: 999, background: isUrgent ? 'linear-gradient(90deg,#ef4444,#dc2626)' : 'linear-gradient(90deg,#f59e0b,#ef4444)', width: `${saleBarFill}%`, transition: 'width .7s cubic-bezier(.4,0,.2,1)' }} />
          </div>
        </div>
      )}

      {/* -- Mode toggle -- */}
      <div className="pp-mode-toggle" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--v4-panel-2)', border: '1px solid var(--v4-border)', borderRadius: 14, padding: 4, position: 'relative' }}>
        {/* Animated pill */}
        <div style={{
          position: 'absolute', top: 4, bottom: 4, left: 4, width: 'calc(50% - 4px)', borderRadius: 10, zIndex: 0,
          background: mode === 'buy' ? 'linear-gradient(135deg,#107c10,#0078d4)' : 'linear-gradient(135deg,#ea6c00,#fb923c)',
          boxShadow: mode === 'buy' ? '0 0 20px rgba(82,176,67,.38)' : '0 0 20px rgba(251,146,60,.38)',
          transform: mode === 'rent' ? 'translateX(100%)' : 'translateX(0)',
          transition: 'transform .28s cubic-bezier(.4,0,.2,1), background .28s',
          willChange: 'transform',
        }} />
        {(['buy', 'rent'] as const).map(m => (
          <button key={m} onClick={() => setMode(m)} className={`pp-mode-btn${mode === m ? ' pp-mode-btn-active' : ''}`} style={{
            position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '11px 8px', border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, borderRadius: 10, touchAction: 'manipulation',
            color: mode === m ? '#fff' : 'var(--v4-fg-3)', transition: 'color .2s',
          }}>
            {m === 'buy' ? <ShoppingCart size={15} /> : <Clock size={15} />}
            {m === 'buy' ? tl('pdp_buy') : tl('pdp_buy_rent')}
          </button>
        ))}
      </div>

      {/* BUY PANEL */}
      <div style={{ display: mode === 'buy' ? 'flex' : 'none', flexDirection: 'column', gap: 16, width: '100%', minWidth: 0 }}>
        <>
          {/* Subtabs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
            {SUBTABS.map(s => {
              const isAct = subTab === s.id;
              return (
                <button key={s.id} onClick={() => setSubTab(s.id)}
                  className={`pp-subtab${isAct ? ' pp-subtab-active' : ''}`}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                    padding: '10px 6px', borderRadius: 12, cursor: 'pointer',
                    border: `1px solid ${isAct ? s.activeBorder : 'transparent'}`,
                    background: isAct ? 'var(--v4-panel-2)' : 'transparent',
                    color: isAct ? 'var(--fg)' : 'var(--v4-fg-3)',
                    fontSize: 11, fontWeight: 700, transition: 'border-color .18s, background .18s, box-shadow .18s, color .18s', position: 'relative',
                    boxShadow: isAct ? `0 0 16px ${s.activeGlow}` : 'none',
                  }}>
                  {/* Discount badge on key tab */}
                  {s.id === 'key' && disc && disc > 0 && (
                    <span style={{ position: 'absolute', top: -8, right: -8, fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 999, border: '1px solid rgba(82,176,67,.45)', background: 'var(--surface)', color: 'var(--gs-green)' }}>
                      -{disc}%
                    </span>
                  )}
                  <s.Icon size={18} />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Delivery speed strip — updates on subTab + region change */}
          {(() => {
            let instant = false;
            let altRegions: string[] = [];
            let labelText = tl('pdp_delivery_24h');
            let DeliveryIcon: React.ElementType = Clock;
            let color = '#4da6ff';
            let bg = 'rgba(77,166,255,.07)';
            let border = 'rgba(77,166,255,.18)';
            if (subTab === 'key') {
              instant = !!(deliveryStock?.key);
              altRegions = (deliveryStock?.key_regions ?? []).filter(r => r !== market);
              if (instant) { labelText = tl('pdp_delivery_instant'); DeliveryIcon = Zap; color = '#52b043'; bg = 'rgba(82,176,67,.12)'; border = 'rgba(82,176,67,.28)'; }
              else if (altRegions.length > 0) { labelText = `${tl('pdp_delivery_in')} ${altRegions.slice(0,4).join(', ')}`; DeliveryIcon = Zap; color = '#facc15'; bg = 'rgba(250,204,21,.09)'; border = 'rgba(250,204,21,.24)'; }
            } else if (subTab === 'gift') {
              labelText = tl('pdp_delivery_24h'); color = '#4da6ff'; bg = 'rgba(77,166,255,.07)'; border = 'rgba(77,166,255,.18)';
            } else if (subTab === 'account') {
              instant = !!(deliveryStock?.account);
              altRegions = (deliveryStock?.account_regions ?? []).filter(r => r !== market);
              if (instant) { labelText = tl('pdp_delivery_instant'); DeliveryIcon = Zap; color = '#52b043'; bg = 'rgba(82,176,67,.12)'; border = 'rgba(82,176,67,.28)'; }
              else if (altRegions.length > 0) { labelText = `${tl('pdp_delivery_in')} ${altRegions.slice(0,4).join(', ')}`; DeliveryIcon = Zap; color = '#facc15'; bg = 'rgba(250,204,21,.09)'; border = 'rgba(250,204,21,.24)'; }
            }
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, background: bg, border: `1px solid ${border}` }}>
                <DeliveryIcon size={12} style={{ color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color }}>{labelText}</span>
              </div>
            );
          })()}

          <hr className="pp-divider" style={{ border: 'none', borderTop: '1px solid var(--v4-border)', margin: 0 }} />

          {/* Region selector */}
          <div>
            <p className="pp-label" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--v4-fg-4)', marginBottom: 8 }}>
              {tl('pdp_activation_region')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button onClick={() => setRegionOpen(!regionOpen)} className="pp-region-btn" style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '11px 14px', borderRadius: regionOpen ? '12px 12px 0 0' : 12,
                border: `1px solid ${regionOpen ? 'rgba(82,176,67,.4)' : 'var(--v4-border)'}`,
                background: regionOpen ? 'rgba(82,176,67,.06)' : 'var(--v4-panel)',
                cursor: 'pointer', transition: 'border-color .18s, background .18s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{meta?.flag ?? '🌍'}</span>
                  <div style={{ textAlign: 'left' }}>
                    <div className="pp-region-meta-label" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--v4-fg-3)' }}>{tl('pdp_selected_region')}</div>
                    <div className="pp-region-name" style={{ fontSize: 13, fontWeight: 800, color: 'var(--v4-fg)' }}>{getRegionName(meta, lang, price?.market_code ?? '—')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {price && <span style={{ fontSize: 16, fontWeight: 900, color: 'var(--gs-price)', fontVariantNumeric: 'tabular-nums' }}>{(() => { const u = getUsd(price, mode === 'rent' ? 'rental' : subTab); if (lang === 'ru' && displaySettings?.showRub) { const r = getRub(u); if (r != null) return `${Math.round(r).toLocaleString('ru-RU')} ₽`; } if (lang === 'uk') { const h = getUah(u); if (h != null) return `${Math.round(h).toLocaleString('uk-UA')} ₴`; } if (u != null) return `$${u.toFixed(2)}`; return '—'; })()}</span>}
                  <ChevronDown size={16} color="var(--v4-fg-3)" style={{ transform: regionOpen ? 'rotate(180deg)' : 'none', transition: 'transform .28s cubic-bezier(.4,0,.2,1)' }} />
                </div>
              </button>

              {regionOpen && (
              <div className="pp-region-dd" style={{
                border: '1px solid rgba(82,176,67,.35)',
                borderTop: 'none', borderRadius: '0 0 12px 12px',
                background: 'var(--v4-bg-elev)',
                maxHeight: 420, overflowY: 'auto',
              }}>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span className="pp-label" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--v4-fg-3)' }}>{tl('pdp_select_region')}</span>
                    <span className="pp-label" style={{ fontSize: 10, color: 'var(--v4-fg-4)' }}>{visiblePrices.length} {tl('pdp_regions_count')}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {visiblePrices.map(p => {
                      const pm = REGION_META[p.market_code];
                      const isAct = p.market_code === market;
                      const pd = p.discount_percent != null ? Math.round(p.discount_percent) : null;
                      return (
                        <button key={p.market_code} onClick={() => { setMarket(p.market_code); setRegionOpen(false); }} style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 8px', borderRadius: 8,
                          border: isAct ? '1px solid rgba(82,176,67,.35)' : '1px solid transparent',
                          background: isAct ? 'rgba(82,176,67,.07)' : 'transparent',
                          cursor: 'pointer', color: 'var(--v4-fg)', textAlign: 'left', transition: 'all .15s',
                        }}>
                          {subTab === 'key' && deliveryStock && (
                            deliveryStock.key_regions.includes(p.market_code)
                              ? <Zap size={11} style={{ flexShrink: 0, color: '#52b043' }} />
                              : <Clock size={11} style={{ flexShrink: 0, color: 'var(--v4-fg-4)' }} />
                          )}
                          {subTab === 'account' && deliveryStock && (
                            deliveryStock.account_regions.includes(p.market_code)
                              ? <Zap size={11} style={{ flexShrink: 0, color: '#52b043' }} />
                              : <Clock size={11} style={{ flexShrink: 0, color: 'var(--v4-fg-4)' }} />
                          )}
                          <span style={{ fontSize: 15, flexShrink: 0, width: 22, textAlign: 'center', lineHeight: 1 }}>{pm?.flag ?? '🌍'}</span>
                          <span className="pp-region-item-name" style={{ fontSize: 12, fontWeight: 700, color: 'var(--v4-fg-2)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getRegionName(pm, lang, p.market_code)}</span>
                          {/* market chips (VPN тощо) — inline */}
                          {(displaySettings?.marketChips?.[p.market_code]?.[lang as 'uk' | 'en'] ?? []).map(chip => (
                            <span key={chip} style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 999, background: 'rgba(251,146,60,.18)', color: '#fb923c', border: '1px solid rgba(251,146,60,.3)', flexShrink: 0 }}>{chip}</span>
                          ))}
                          {/* discount — fixed-width column so every -%% lines up */}
                          <span style={{ width: 46, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
                            {pd && pd > 0 && <span style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 999, background: 'rgba(74,222,128,.18)', color: '#4ade80', border: '1px solid rgba(74,222,128,.3)' }}>-{pd}%</span>}
                          </span>
                          {/* price — fixed-width column, right-aligned + tabular so every price lines up */}
                          <span style={{ width: 84, flexShrink: 0, textAlign: 'right', fontSize: 12, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: 'var(--gs-price)' }}>
                            {(() => {
                              const u2 = getUsd(p, mode === 'rent' ? 'rental' : subTab);
                              if (lang === 'ru' && displaySettings?.showRub) { const r = getRub(u2); if (r != null) return `${Math.round(r).toLocaleString('ru-RU')} ₽`; }
                              if (lang === 'uk') { const h = getUah(u2); if (h != null) return `${Math.round(h).toLocaleString('uk-UA')} ₴`; }
                              if (u2 != null) return `$${u2.toFixed(2)}`;
                              return '—';
                            })()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {(displaySettings?.marketChips?.[price?.market_code ?? '']?.[lang as 'uk' | 'en'] ?? []).some(c => c.toUpperCase().includes('VPN')) && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '8px 10px', borderRadius: 8, background: 'rgba(251,146,60,.07)', border: '1px solid rgba(251,146,60,.22)', fontSize: 10, color: 'rgba(251,146,60,.9)', lineHeight: 1.5 }}>
                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1, color: '#fb923c' }} />
                      <span>{tl('pdp_vpn_note')}</span>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>

          <hr className="pp-divider" style={{ border: 'none', borderTop: '1px solid var(--v4-border)', margin: 0 }} />

          {/* Price */}
          {price && (() => {
            const offerKey = mode === 'rent' ? 'rental' : subTab;
            const rentalUsd = displaySettings?.rentalSettings
              ? getUsd(prices.find(p => p.market_code === displaySettings.rentalSettings!.base_region) ?? price, 'rental')
              : getUsd(price, 'rental');
            const effectiveUsd = offerKey === 'rental'
              ? (rentalUsd != null ? rentalUsd * rentalOpt.mult : null)
              : getUsd(price, offerKey);
            const cvt = (usd: number | null) => {
              if (usd == null) return null;
              if (lang === 'ru' && displaySettings?.showRub) { const r = getRub(usd); if (r != null) return `${Math.round(r).toLocaleString('ru-RU')} ₽`; }
              if (lang === 'uk') { const h = getUah(usd); if (h != null) return `${Math.round(h).toLocaleString('uk-UA')} ₴`; }
              return `$${usd.toFixed(2)}`;
            };
            const mainStr = cvt(effectiveUsd) ?? (effectiveUsd != null ? `$${effectiveUsd.toFixed(2)}` : '—');
            const msrpUsd = msrpUsdFromDiscount(effectiveUsd, price.discount_percent);
            const msrpStr = msrpUsd != null ? (cvt(msrpUsd) ?? `$${msrpUsd.toFixed(2)}`) : null;
            return (
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                  <div className="pp-price-main" style={{ fontSize: 32, fontWeight: 900, color: 'var(--gs-price)', fontVariantNumeric: 'tabular-nums' }}>
                    {mainStr}
                  </div>
                  {msrpStr && (
                    <div className="pp-price-orig" style={{ fontSize: 13, color: 'var(--gs-price-old)', textDecoration: 'line-through', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>
                      {msrpStr}
                    </div>
                  )}
                  {disc && disc > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999, marginBottom: 4, background: 'var(--gs-green-grad)', color: '#fff', boxShadow: 'var(--v4-shadow-btn-emerald)' }}>
                      -{disc}%
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Features */}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {st.features.map(f => (
              <li key={f} className="pp-feat-item" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v4-fg-2)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.featColor, flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>

          {/* CTA row */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAddToCart} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: 15, border: 'none', borderRadius: 12, cursor: 'pointer',
              background: added ? 'var(--gs-green-grad)' : st.ctaBg,
              fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '.03em',
              boxShadow: `0 0 22px ${ctaGlow}`, transition: 'all .2s',
            }}>
              {added ? <><Check size={16} />{tl('pdp_added')}</> : <><st.ctaIcon size={16} />{st.ctaLabel}</>}
            </button>
            <button onClick={handleWishlist} aria-label="До списку бажань"
              className={`pp-wish-btn${wished ? ' wished' : ''}`}
              style={{
                flexShrink: 0, width: 48, alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 12, border: wished ? '1px solid rgba(239,68,68,.4)' : '1px solid var(--v4-border)',
                background: wished ? 'rgba(239,68,68,.1)' : 'var(--v4-panel)', cursor: 'pointer', transition: 'border-color .18s, background .18s',
              }}>
              <Heart size={16} color={wished ? '#ef4444' : 'var(--v4-fg-3)'} fill={wished ? '#ef4444' : 'none'} />
            </button>
          </div>

          <p className="pp-footer-text" style={{ textAlign: 'center', fontSize: 10, color: 'var(--v4-fg-4)', margin: 0 }}>
            {tl('pdp_footer_buy')}
          </p>
        </>
      </div>

      {/* RENT PANEL */}
      <div style={{ display: mode === 'rent' ? 'flex' : 'none', flexDirection: 'column', gap: 16, width: '100%', minWidth: 0 }}>
        <>
          <div>
            <p className="pp-label" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--v4-fg)', margin: 0 }}>
              {tl('pdp_rental_duration')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <p className="pp-label" style={{ fontSize: 11, color: 'var(--v4-fg-3)', margin: 0 }}>
                {tl('pdp_rental_access')}
              </p>
              {deliveryStock && (
                <span style={{ fontSize: 10, fontWeight: 700, color: deliveryStock.rental ? '#fb923c' : '#4da6ff', padding: '2px 8px', borderRadius: 999, background: deliveryStock.rental ? 'rgba(251,146,60,.12)' : 'rgba(77,166,255,.07)', border: `1px solid ${deliveryStock.rental ? 'rgba(251,146,60,.3)' : 'rgba(77,166,255,.18)'}`, whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                  {deliveryStock.rental ? <><Zap size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />{tl('pdp_delivery_instant').split('—')[1]?.trim() ?? tl('pdp_delivery_instant')}</> : <><Clock size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />{tl('pdp_delivery_24h')}</>}
                </span>
              )}
            </div>
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7 }}>
            {activeRentalOpts.map(opt => {
              const isAct = rentalDays === opt.days;
              // Use API-calculated price/day if available
              const apiOpt = apiRentalOpts?.find(o => o.days === opt.days);
              const perDayDisplay = apiOpt?.price_per_day != null
                ? (cvtFromUsd(apiOpt.price_per_day) ?? `$${apiOpt.price_per_day.toFixed(2)}`)
                : (() => { const b = price?.prices_usd?.rental; if (b == null) return ''; const pd = b * opt.mult / opt.days; return cvtFromUsd(pd) ?? `$${pd.toFixed(2)}`; })();
              const badgeText = opt.popular ? tl('pdp_popular') : null;
              return (
                <button key={opt.days} onClick={() => setRentalDays(opt.days)}
                  className={`pp-rent-day${isAct ? ' pp-rent-day-active' : ''}`}
                  style={{
                    padding: '10px 5px', borderRadius: 12, cursor: 'pointer', textAlign: 'center', position: 'relative',
                    border: isAct ? '1px solid rgba(251,146,60,.5)' : '1px solid var(--v4-border)',
                    background: isAct ? 'rgba(251,146,60,.1)' : 'var(--v4-panel)',
                    boxShadow: isAct ? '0 0 14px rgba(251,146,60,.25)' : 'none',
                    color: 'var(--v4-fg)', transition: 'border-color .18s, background .18s, box-shadow .18s',
                  }}>
                  {badgeText && (
                    <span style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', fontSize: 8, fontWeight: 800, background: '#ea6c00', color: '#fff', padding: '2px 6px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                      {badgeText}
                    </span>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{opt.days} {tl('pdp_rental_days_long')}</div>
                  {perDayDisplay && <div className="pp-label" style={{ fontSize: 9, color: 'var(--v4-fg-3)', marginTop: 2 }}>{perDayDisplay}{tl('pdp_per_day')}</div>}
                </button>
              );
            })}
          </div>

          <hr className="pp-divider" style={{ border: 'none', borderTop: '1px solid var(--v4-border)', margin: 0 }} />

          {/* Rent price */}
          {price && (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                <div className="pp-price-main" style={{ fontSize: 32, fontWeight: 900, color: 'var(--gs-price)', fontVariantNumeric: 'tabular-nums' }}>
                  {apiCurrentOpt?.price_usd != null
                    ? (cvtFromUsd(apiCurrentOpt.price_usd) ?? `$${apiCurrentOpt.price_usd.toFixed(2)}`)
                    : (cvtFromUsd(rentalPrice) ?? `$${rentalPrice.toFixed(2)}`)}
                </div>
                {apiCurrentOpt?.original_price != null && (
                  <div className="pp-price-orig" style={{ fontSize: 13, color: 'var(--gs-price-old)', textDecoration: 'line-through', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>
                    ${apiCurrentOpt.original_price.toFixed(2)}
                  </div>
                )}
                {rentSavings > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999, marginBottom: 4, background: 'rgba(251,146,60,.15)', border: '1px solid rgba(251,146,60,.3)', color: '#fb923c' }}>
                    -{rentSavings}%
                  </div>
                )}
              </div>
              <p className="pp-label" style={{ fontSize: 11, color: 'var(--v4-fg-3)', marginTop: 4 }}>
                {tl('pdp_rental_for')} {rentalDays} {tl('pdp_rental_days_long')} · {apiCurrentOpt?.price_per_day != null
                  ? (cvtFromUsd(apiCurrentOpt.price_per_day) ?? `$${apiCurrentOpt.price_per_day.toFixed(2)}`)
                  : (cvtFromUsd(rentalPerDay) ?? `$${rentalPerDay.toFixed(2)}`)}{tl('pdp_per_day')}
              </p>
            </div>
          )}

          {/* Features */}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {RENT_FEATURES.map(f => (
              <li key={f} className="pp-feat-item" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v4-fg-2)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fb923c', flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>

          {/* CTA row */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAddToCart} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: 15, border: 'none', borderRadius: 12, cursor: 'pointer',
              background: added ? 'var(--gs-green-grad)' : '#ea6c00',
              fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '.03em',
              boxShadow: `0 0 22px ${ctaGlow}`, transition: 'all .2s',
            }}>
              {added ? <><Check size={16} />{tl('pdp_added')}</> : <><Clock size={16} />{tl('pdp_buy_rent')} {tl('pdp_rental_for')} {rentalDays} {tl('pdp_rental_days_long')}</>}
            </button>
            <button onClick={handleWishlist} aria-label="До списку бажань"
              className={`pp-wish-btn${wished ? ' wished' : ''}`}
              style={{
                flexShrink: 0, width: 48, alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 12, border: wished ? '1px solid rgba(239,68,68,.4)' : '1px solid var(--v4-border)',
                background: wished ? 'rgba(239,68,68,.1)' : 'var(--v4-panel)', cursor: 'pointer', transition: 'border-color .18s, background .18s',
              }}>
              <Heart size={16} color={wished ? '#ef4444' : 'var(--v4-fg-3)'} fill={wished ? '#ef4444' : 'none'} />
            </button>
          </div>

          <p className="pp-footer-text" style={{ textAlign: 'center', fontSize: 10, color: 'var(--v4-fg-4)', margin: 0 }}>
            {tl('pdp_footer_rent')}
          </p>
        </>
      </div>

    </div>
  );
}

